import React, { useMemo, useState } from 'react';
import { Upload } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import cn from './styles.module.scss';

type ImageUploadProps = {
  defaultImage?: string,
  onRequest: (file: UploadFile, setProgress: (progress: number) => void) => Promise<string>,
  onChange: (file: UploadFile) => void;
};

export enum UploadStatus {
  Done = 'done',
  Uploading = 'uploading',
  Error = 'error',
}

export const ImageUpload = ({ defaultImage, onRequest, onChange }: ImageUploadProps) => {
  const [image, setImage] = useState<any>({
    status: defaultImage ? UploadStatus.Done : undefined,
    percent: defaultImage ? 100 : 0,
    url: defaultImage,
  });
  const [isLoading, setIsLoading] = useState(false);

  const setProgress = (percent: number) => {
    setImage({
      ...image,
      status: UploadStatus.Uploading,
      percent,
    });
  };

  const handleRequest = async ({ file }: { file: UploadFile }) => {
    try {
      setIsLoading(true);
      const result = await onRequest(file, setProgress);
      setImage({
        ...image,
        url: result,
        status: UploadStatus.Done,
      });
    } catch {
      setImage({
        ...image,
        status: UploadStatus.Error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = ({ fileList }: { fileList: UploadFile[] }) => {
    setImage({
      ...image,
      status: UploadStatus.Uploading,
    });
    if (onChange) {
      onChange(fileList[0]);
    }
  };

  const content = useMemo(() => {
    if (image.url && image.status === UploadStatus.Done) {
      return <img src={image.url} alt="image_preview" className={cn.image} />;
    }

    return (
      <div
        className={cn.content}
      >
        {isLoading ? <LoadingOutlined /> : <PlusOutlined />}
        <div className={cn.text}>{isLoading ? 'Uploading...' : 'Upload'}</div>
      </div>
    );
  }, [image, isLoading]);

  return (
    <Upload
      listType="picture-card"
      showUploadList={false}
      customRequest={handleRequest as any}
      onChange={handleChange}
      fileList={[image]}
      multiple={false}
    >
      {content}
    </Upload>
  );
};
